import React from "react";
import { Shield, Sparkles, Droplets, Award } from "lucide-react";

const scrollToForm = (voucherId) => {
  const el = document.getElementById("rezervace");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (voucherId) {
      window.dispatchEvent(new CustomEvent("carspa:select-voucher", { detail: voucherId }));
    }
  }
};

const VOUCHERS = [
  {
    id: "essential",
    name: "Essential",
    tagline: "Péče pro každý den",
    price: "2 999",
    value: "4 499",
    save: "1 500",
    highlights: ["Ruční mytí exteriéru + disky", "Důkladné vysátí celého vozu", "Tepování sedadel + kobereček", "Profi čištění kůže + plastů"],
  },
  {
    id: "prestige",
    name: "Prestige",
    tagline: "Přeměna laku + Koch keramika",
    price: "6 999",
    value: "10 499",
    save: "3 500",
    badge: "Nejoblíbenější",
    highlights: ["Dekontaminace laku", "Dvoukrokové rozleštění", "Koch sklokeramika · záruka 2 roky", "Hydrofobní skla + plasty"],
  },
  {
    id: "supreme",
    name: "Supreme",
    tagline: "Exteriér + interiér v jednom",
    price: "9 999",
    value: "14 999",
    save: "5 000",
    highlights: ["Vše z Essential + Prestige", "Prioritní termín", "Osobní konzultace", "Koch sklokeramika · 2 roky"],
  },
  {
    id: "allin",
    name: "All In",
    tagline: "Maximální transformace. Vše chráněno.",
    price: "13 990",
    value: "20 990",
    save: "7 000",
    badge: "Top",
    featured: true,
    highlights: [
      "Vše ze Supreme (lak + interiér + 2letá záruka)",
      "Gyeon Q² LeatherShield — keramika kůže (1 rok)",
      "Gyeon Q² Trim — keramika plastů a těsnění",
      "Ozonování interiéru — pachů a bakterií zbaveno",
      "Prioritní termín v Čestlicích i Butovicích",
    ],
  },
];

const CERAMIC_BENEFITS = [
  { icon: Shield, title: "Lak chráněný 2–3 roky", body: "Sklokeramika Koch tvoří tvrdou vrstvu odolnou proti UV, ptačímu trusu a chemii." },
  { icon: Sparkles, title: "Hloubka & mokrý lesk", body: "Lak vypadá jako čerstvě nalakovaný — i po 24 měsících provozu." },
  { icon: Droplets, title: "Voda steče, špína nedrží", body: "Hydrofobní efekt — drobné nečistoty se nezachytí, mytí je dvakrát rychlejší." },
  { icon: Award, title: "Premium chemie", body: "Pracujeme s německou Koch Chemie a korejským Gyeon — to samé používají top dealeři." },
];

export const Hero = ({ remaining, total }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/14231672/pexels-photo-14231672.jpeg?auto=compress&cs=tinysrgb&w=1920&q=80"
          alt="Detailing černého sportovního vozu v CarSpa"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/85" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        {/* Compact headline */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-10">
          <div
            data-testid="hero-scarcity-tag"
            className="inline-flex items-center gap-2 bg-hormozi-yellow px-3 py-1.5 mb-4 sm:mb-5"
          >
            <span className="w-1.5 h-1.5 bg-black rounded-full animate-gold-pulse" />
            <span className="text-[11px] sm:text-xs tracking-wide uppercase font-bold text-black">
              Zbývá {remaining ?? "—"} z {total ?? 45} voucherů
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-white tracking-tight"
          >
            Minimální cena.<br />
            <span className="text-hormozi-yellow">Maximální výsledek.</span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-4 sm:mt-5 text-base sm:text-lg text-zinc-300 leading-snug font-medium"
          >
            Prémiový detailing s chemií <span className="text-white font-bold">Koch Chemie</span> a <span className="text-white font-bold">Gyeon</span>.
            Vyberte si voucher a ušetřete až <span className="text-hormozi-yellow font-black">7 000 Kč</span>.
          </p>
        </div>

        {/* Ceramic coating demo video — visual proof */}
        <div
          data-testid="hero-ceramic-demo"
          className="max-w-5xl mx-auto mb-10 md:mb-14"
        >
          <div className="grid grid-cols-12 gap-5 sm:gap-6 md:gap-8 items-center">
            <div className="col-span-12 sm:col-span-5 lg:col-span-4">
              <div className="relative border-2 border-hormozi-yellow bg-black mx-auto max-w-[280px] sm:max-w-none">
                <video
                  data-testid="hero-ceramic-video"
                  className="w-full h-auto block"
                  src="/showcase/ceramic.mp4"
                  poster="/showcase/ceramic-poster.jpg"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                />
                <div className="absolute top-3 left-3 bg-black/80 backdrop-blur-sm px-2.5 py-1 border-l-2 border-hormozi-yellow">
                  <span className="text-[9px] sm:text-[10px] tracking-wide uppercase font-black text-white">
                    Reálný záběr · po měsících
                  </span>
                </div>
              </div>
            </div>
            <div className="col-span-12 sm:col-span-7 lg:col-span-8 text-center sm:text-left">
              <div className="inline-flex items-center gap-2 bg-hormozi-yellow text-black px-3 py-1.5 mb-3 sm:mb-4">
                <span className="text-[10px] sm:text-[11px] tracking-wide uppercase font-black">
                  ▲ Takhle funguje keramika
                </span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl md:text-4xl text-white leading-[1] tracking-tight">
                Voda steče, špína nedrží.<br />
                <span className="text-hormozi-yellow">I po několika měsících.</span>
              </h3>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base text-zinc-300 leading-snug font-medium max-w-md mx-auto sm:mx-0">
                Tohle není první týden po aplikaci. Tohle je
                <span className="text-white font-bold"> reálný stav po několika měsících provozu</span> —
                lak chráněný Koch sklokeramikou se sám čistí při každém dešti.
              </p>
            </div>
          </div>
        </div>

        {/* 4 VOUCHER CARDS — main attraction */}
        <div
          data-testid="hero-voucher-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-10 md:mb-14"
        >
          {VOUCHERS.map((v) => (
            <div
              key={v.id}
              data-testid={`hero-voucher-${v.id}`}
              className={`relative flex flex-col p-5 sm:p-6 bg-black/70 backdrop-blur-sm transition-transform hover:-translate-y-1 ${
                v.featured
                  ? "border-2 border-hormozi-yellow shadow-[0_0_40px_-10px_rgba(255,212,59,0.5)]"
                  : "border border-white/15"
              }`}
            >
              {v.badge && (
                <div className="absolute -top-3 left-5 bg-hormozi-yellow text-black text-[10px] sm:text-[11px] tracking-wide uppercase font-black px-2.5 py-1">
                  {v.badge}
                </div>
              )}

              <div className={`text-[10px] sm:text-[11px] tracking-wide uppercase font-bold mb-1 ${v.featured ? "text-hormozi-yellow" : "text-zinc-500"}`}>
                {v.name}
              </div>
              <div className="text-sm text-zinc-300 leading-tight mb-4 font-medium min-h-[2.5rem]">{v.tagline}</div>

              {/* Pricing */}
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-display text-3xl sm:text-4xl text-white leading-none tabular-nums">
                  {v.price}
                </span>
                <span className="text-sm text-zinc-400">Kč</span>
              </div>
              <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5 mb-3 sm:mb-4">
                <span className="text-xs text-zinc-500 line-through decoration-red-500/70 decoration-2 tabular-nums">{v.value} Kč</span>
                <span className={`text-[11px] font-black ${v.featured ? "text-hormozi-yellow" : "text-zinc-300"}`}>
                  Ušetříte {v.save} Kč
                </span>
              </div>

              <ul className="space-y-2 mb-5 flex-1">
                {v.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-[12px] sm:text-[13px] text-zinc-300 leading-snug">
                    <span className="text-hormozi-yellow font-black mt-0.5 shrink-0">✓</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => scrollToForm(v.id)}
                data-testid={`hero-voucher-cta-${v.id}`}
                className={`w-full px-4 py-3 text-[11px] sm:text-xs tracking-tight uppercase font-black transition-colors ${
                  v.featured
                    ? "bg-hormozi-yellow hover:bg-[#FFE34D] text-black"
                    : "bg-white/10 hover:bg-hormozi-yellow hover:text-black text-white border border-white/20"
                }`}
              >
                Chci {v.name} →
              </button>
            </div>
          ))}
        </div>

        {/* Ceramic benefits row */}
        <div className="border-2 border-hormozi-yellow/60 bg-black/60 backdrop-blur-sm">
          <div className="bg-hormozi-yellow text-black px-5 sm:px-6 py-3 flex items-center justify-between flex-wrap gap-2">
            <span className="text-xs sm:text-sm tracking-wide uppercase font-black">
              ▲ Co dělá keramická ochrana s vaším autem
            </span>
            <span className="text-[10px] sm:text-[11px] tracking-wide uppercase font-bold">
              Koch Chemie · Gyeon
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/10 lg:divide-x">
            {CERAMIC_BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="p-5 sm:p-6" data-testid={`hero-ceramic-${i}`}>
                  <Icon size={22} strokeWidth={1.6} className="text-hormozi-yellow mb-3" />
                  <div className="font-bold text-white text-sm sm:text-base mb-1.5 leading-tight">{b.title}</div>
                  <div className="text-xs sm:text-[13px] text-zinc-400 leading-snug">{b.body}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-7 sm:mt-8 flex flex-wrap items-center justify-center gap-x-5 sm:gap-x-7 gap-y-2 text-[11px] sm:text-xs text-zinc-400 font-medium">
          <span>✓ 10 let praxe</span>
          <span className="text-zinc-700">•</span>
          <span>✓ 2 pobočky v Praze</span>
          <span className="text-zinc-700">•</span>
          <span>✓ Voucher přenositelný</span>
          <span className="text-zinc-700">•</span>
          <span>✓ Platnost 3 roky</span>
          <span className="text-zinc-700">•</span>
          <span>✓ Záruka 2 roky na keramiku</span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
